import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import ValidationRuleTable from '../components/validationRule/ValidationRuleTable';
import ValidationRuleForm from '../components/validationRule/ValidationRuleForm';
import ValidationRuleService from '../services/validationRuleService';
import financialRecordKPIService from '../services/financialRecordKPIService';
import { ValidationRule } from '../types/ValidationRule';

const KPIMaintenancePage: React.FC = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState<ValidationRule[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<ValidationRule | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  
  // Brand selection state
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [loadingBrands, setLoadingBrands] = useState<boolean>(true);
  const [brandError, setBrandError] = useState<string | null>(null);
  
  // KPI display state
  interface BrandKPI {
    code: string;
    description: string;
    category: string;
    unit: string;
    recordCount: number;
    sampleValues: any[];
  }
  
  const [brandKPIs, setBrandKPIs] = useState<BrandKPI[]>([]);
  const [loadingKPIs, setLoadingKPIs] = useState<boolean>(false);
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [selectedKPIs, setSelectedKPIs] = useState<BrandKPI[]>([]);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  const fetchRules = async () => {
    setLoading(true);
    try {
      const data = await ValidationRuleService.getUserValidationRules();
      setRules(data);
      setError(null);
    } catch (err) {
      setError('Error loading validation rules. Please try again later.');
      console.error('Error fetching validation rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableBrands = async () => {
    setLoadingBrands(true);
    setBrandError(null);
    try {
      // Use the same approach as Analytics page - fetch filter options
      const response = await financialRecordKPIService.getFilterOptions();
      console.log('Filter options response:', response);
      
      if (response && response.brands) {
        setAvailableBrands(response.brands);
        
        // Auto-select first brand if available
        if (response.brands.length > 0 && !selectedBrand) {
          setSelectedBrand(response.brands[0]);
        }
      } else {
        console.warn('No brands found in filter options response');
        setAvailableBrands([]);
      }
    } catch (err) {
      setBrandError('Error loading available brands. Please try again later.');
      console.error('Error fetching available brands:', err);
      // Log the full error for debugging
      if (err instanceof Error) {
        console.error('Full error details:', {
          message: err.message,
          stack: err.stack,
          name: err.name
        });
      }
    } finally {
      setLoadingBrands(false);
    }
  };

  const fetchBrandKPIs = async (brand: string) => {
    if (!brand) {
      setBrandKPIs([]);
      return;
    }

    setLoadingKPIs(true);
    setKpiError(null);
    try {
      console.log('🚀 PERFORMANCE: Fetching KPIs for brand using optimized API:', brand);
      
      // Use the new optimized brand KPIs API
      const response = await financialRecordKPIService.getBrandKPIs(brand);
      
      console.log('✅ PERFORMANCE: Brand KPIs response:', response);
      
      // Convert the response to the format expected by the UI
      const uniqueKPIs: BrandKPI[] = response.kpis.map(kpi => ({
        code: kpi.code,
        description: kpi.description,
        category: kpi.category,
        unit: kpi.unit,
        recordCount: kpi.recordCount,
        sampleValues: kpi.sampleValues
      }));
      
      console.log(`✅ PERFORMANCE: Found ${uniqueKPIs.length} unique KPIs for brand ${brand} (optimized)`);
      setBrandKPIs(uniqueKPIs);
      
      // Set all categories as collapsed by default
      const categories = Array.from(new Set(uniqueKPIs.map(kpi => kpi.category)));
      setCollapsedCategories(new Set(categories));
      
    } catch (err) {
      console.error('❌ Error fetching brand KPIs:', err);
      setKpiError('Error loading KPIs for selected brand. Please try again later.');
      setBrandKPIs([]);
      
      // Note: Could implement fallback to old method here if needed
      console.log('⚠️ New API failed, no fallback implemented yet');
    } finally {
      setLoadingKPIs(false);
    }
  };

  useEffect(() => {
    fetchRules();
    fetchAvailableBrands();
  }, []);

  // Fetch KPIs when selected brand changes
  useEffect(() => {
    if (selectedBrand) {
      fetchBrandKPIs(selectedBrand);
    } else {
      setBrandKPIs([]);
      setSelectedKPIs([]);
      setCollapsedCategories(new Set());
    }
  }, [selectedBrand]);

  const toggleCategoryCollapse = (category: string) => {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(category)) {
      newCollapsed.delete(category);
    } else {
      newCollapsed.add(category);
    }
    setCollapsedCategories(newCollapsed);
  };

  const toggleKPISelection = (kpi: BrandKPI) => {
    const isSelected = selectedKPIs.some(selected => selected.code === kpi.code && selected.category === kpi.category);
    if (isSelected) {
      setSelectedKPIs(selectedKPIs.filter(selected => !(selected.code === kpi.code && selected.category === kpi.category)));
    } else {
      setSelectedKPIs([...selectedKPIs, kpi]);
    }
  };

  const isKPISelected = (kpi: BrandKPI) => {
    return selectedKPIs.some(selected => selected.code === kpi.code && selected.category === kpi.category);
  };

  const handleCreateRule = () => {
    if (!selectedBrand) {
      setError('Please select a brand before creating a validation rule.');
      return;
    }
    if (selectedKPIs.length === 0) {
      setError('Please select at least one KPI before creating a validation rule.');
      return;
    }
    setEditingRule(null);
    setIsFormOpen(true);
  };

  const handleEditRule = (rule: ValidationRule) => {
    setEditingRule(rule);
    setIsFormOpen(true);
  };

  const handleDeleteRule = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this validation rule?')) {
      return;
    }

    try {
      await ValidationRuleService.deleteValidationRule(id);
      // Refresh the validation rules list
      fetchRules();
    } catch (err: any) {
      setError('Error deleting validation rule. Please try again.');
      console.error('Error deleting validation rule:', err);
    }
  };

  const handleFormSubmit = async (rule: ValidationRule) => {
    try {
      if (editingRule) {
        // Update existing validation rule
        await ValidationRuleService.updateValidationRule(editingRule.id!, rule);
      } else {
        // Create new validation rule
        await ValidationRuleService.createValidationRule(rule);
      }

      // Close the form and refresh validation rules
      setIsFormOpen(false);
      setEditingRule(null);
      fetchRules();
    } catch (err: any) {
      setError(err.message || 'Error saving validation rule. Please try again.');
      console.error('Error saving validation rule:', err);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingRule(null);
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={goToDashboard}>
              Dashboard
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Validation Rules</span>
          </div>

          <div className="content-container">
            <div className="page-header">
              <h1>Validation Rules</h1>
            </div>

            {/* Brand Selection Section */}
            <div className="brand-selection-section" style={{ marginBottom: '25px' }}>
              <h3>Select Brand</h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '15px' }}>
                Choose a brand to create validation rules for. Only brands with available financial data are shown.
              </p>
              
              {loadingBrands ? (
                <div className="loading-state" style={{ padding: '20px', textAlign: 'center' }}>
                  <div className="spinner"></div>
                  <p>Loading available brands...</p>
                </div>
              ) : brandError ? (
                <div className="error-message">
                  {brandError}
                </div>
              ) : availableBrands.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px', textAlign: 'center' }}>
                  <div className="empty-state-icon">🏢</div>
                  <h4>No Brands Available</h4>
                  <p>No financial data found for any brands. Please upload financial data first.</p>
                </div>
              ) : (
                <div className="brand-selector">
                  <select 
                    value={selectedBrand} 
                    onChange={(e) => {
                      setSelectedBrand(e.target.value);
                      // KPIs will be fetched automatically via useEffect
                    }}
                    className="brand-select"
                    style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '2px solid rgba(0, 200, 255, 0.3)',
                      background: 'rgba(0, 20, 40, 0.8)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '16px',
                      minWidth: '200px',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <option value="">Select a brand...</option>
                    {availableBrands.map((brand) => (
                      <option key={brand} value={brand}>
                        {brand}
                      </option>
                    ))}
                  </select>
                  {selectedBrand && (
                    <div style={{ 
                      marginTop: '10px', 
                      padding: '10px 15px', 
                      background: 'rgba(0, 200, 255, 0.1)', 
                      borderRadius: '6px',
                      border: '1px solid rgba(0, 200, 255, 0.3)'
                    }}>
                      <strong>Selected Brand:</strong> {selectedBrand}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* KPI Selection Section */}
            {selectedBrand && (
              <div className="kpi-selection-section" style={{ marginBottom: '25px' }}>
                <h3>Select KPI for Validation Rule</h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '15px' }}>
                  Choose a specific KPI to create a validation rule for. KPIs are organized by category.
                </p>
                
                {loadingKPIs ? (
                  <div className="loading-state" style={{ padding: '20px', textAlign: 'center' }}>
                    <div className="spinner"></div>
                    <p>Loading KPIs for {selectedBrand}...</p>
                  </div>
                ) : kpiError ? (
                  <div className="error-message">
                    {kpiError}
                  </div>
                ) : brandKPIs.length === 0 ? (
                  <div className="empty-state" style={{ padding: '20px', textAlign: 'center' }}>
                    <div className="empty-state-icon">📊</div>
                    <h4>No KPIs Found</h4>
                    <p>No financial data with KPIs found for {selectedBrand}.</p>
                  </div>
                ) : (
                  <div className="kpi-categories" style={{ 
                    marginTop: '15px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '15px',
                    alignItems: 'start',
                    width: '100%'
                  }}>
                    {(() => {
                      // Group KPIs by category
                      const kpisByCategory = brandKPIs.reduce((acc, kpi) => {
                        const category = kpi.category || 'Uncategorized';
                        if (!acc[category]) acc[category] = [];
                        acc[category].push(kpi);
                        return acc;
                      }, {} as Record<string, BrandKPI[]>);

                      return Object.entries(kpisByCategory).map(([category, kpis]: [string, BrandKPI[]]) => (
                        <div key={category} className="kpi-category" style={{
                          background: 'rgba(0, 20, 40, 0.6)',
                          borderRadius: '8px',
                          border: '1px solid rgba(0, 200, 255, 0.3)',
                          overflow: 'hidden',
                          height: 'fit-content',
                          width: '100%',
                          minWidth: '320px',
                          maxWidth: '100%'
                        }}>
                          <div 
                            style={{
                              padding: '12px 12px',
                              background: 'rgba(0, 200, 255, 0.1)',
                              borderBottom: collapsedCategories.has(category) ? 'none' : '1px solid rgba(0, 200, 255, 0.3)',
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              boxSizing: 'border-box',
                              minHeight: '50px',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease'
                            }}
                            onClick={() => toggleCategoryCollapse(category)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(0, 200, 255, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(0, 200, 255, 0.1)';
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              flex: 1
                            }}>
                              <div style={{
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: 'rgba(0, 200, 255, 0.9)',
                                lineHeight: '1.3',
                                marginBottom: '4px',
                                textAlign: 'left',
                                hyphens: 'auto',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {category}
                              </div>
                              <div style={{
                                fontSize: '12px',
                                fontWeight: 'normal',
                                opacity: 0.8,
                                color: 'rgba(0, 200, 255, 0.7)'
                              }}>
                                {kpis.length} KPIs
                              </div>
                            </div>
                            <div style={{
                              marginLeft: '10px',
                              fontSize: '16px',
                              color: 'rgba(0, 200, 255, 0.9)',
                              fontWeight: 'bold',
                              transition: 'transform 0.2s ease',
                              transform: collapsedCategories.has(category) ? 'rotate(0deg)' : 'rotate(90deg)'
                            }}>
                              ▶
                            </div>
                          </div>
                          {!collapsedCategories.has(category) && (
                            <div style={{ 
                              padding: '8px',
                              display: 'grid',
                              gap: '6px'
                            }}>
                              {kpis.map((kpi, index) => (
                              <div 
                                key={index} 
                                className="kpi-option" 
                                onClick={() => toggleKPISelection(kpi)}
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: '4px',
                                  border: isKPISelected(kpi) ? '2px solid rgba(0, 200, 255, 0.8)' : '1px solid rgba(255, 255, 255, 0.1)',
                                  background: isKPISelected(kpi) ? 'rgba(0, 200, 255, 0.2)' : 'rgba(0, 20, 40, 0.4)',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isKPISelected(kpi)) {
                                    e.currentTarget.style.background = 'rgba(0, 200, 255, 0.1)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isKPISelected(kpi)) {
                                    e.currentTarget.style.background = 'rgba(0, 20, 40, 0.4)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  marginBottom: '8px'
                                }}>
                                  <div style={{
                                    fontSize: '11px',
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    padding: '2px 6px',
                                    borderRadius: '3px'
                                  }}>
                                    {kpi.code}
                                  </div>
                                  {kpi.unit && (
                                    <div style={{
                                      fontSize: '11px',
                                      color: 'rgba(255, 255, 255, 0.6)',
                                      background: 'rgba(255, 255, 255, 0.1)',
                                      padding: '2px 6px',
                                      borderRadius: '3px',
                                      marginLeft: '8px'
                                    }}>
                                      {kpi.unit}
                                    </div>
                                  )}
                                </div>
                                <div style={{
                                  fontWeight: 'bold',
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  fontSize: '14px',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  overflow: 'visible',
                                  textOverflow: 'unset',
                                  minWidth: 0,
                                  lineHeight: '1.3',
                                  marginBottom: '4px'
                                }}>
                                  {kpi.description || 'Unnamed KPI'}
                                </div>
                                <div style={{
                                  fontSize: '11px',
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  marginBottom: '4px',
                                  lineHeight: '1.2',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  overflow: 'visible'
                                }}>
                                  {kpi.description}
                                </div>
                                {kpi.sampleValues.length > 0 && (
                                  <div style={{
                                    fontSize: '11px',
                                    color: 'rgba(0, 200, 255, 0.7)',
                                    background: 'rgba(0, 200, 255, 0.1)',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    marginTop: '4px'
                                  }}>
                                    <strong>Sample:</strong> {kpi.sampleValues.slice(0, 3).join(', ')}
                                    {kpi.sampleValues.length > 3 && '...'}
                                  </div>
                                )}
                                {isKPISelected(kpi) && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    background: 'rgba(0, 200, 255, 0.8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}>
                                    ✓
                                  </div>
                                )}
                              </div>
                            ))}
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                )}
                
                {selectedKPIs.length > 0 && (
                  <div style={{
                    marginTop: '15px',
                    padding: '12px 16px',
                    background: 'rgba(0, 200, 255, 0.15)',
                    borderRadius: '8px',
                    border: '2px solid rgba(0, 200, 255, 0.4)'
                  }}>
                    <strong>Selected KPIs ({selectedKPIs.length}):</strong>
                    <div style={{ marginTop: '8px' }}>
                      {selectedKPIs.map((kpi, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 8px',
                          marginBottom: '4px',
                          background: 'rgba(0, 20, 40, 0.3)',
                          borderRadius: '4px',
                          border: '1px solid rgba(0, 200, 255, 0.2)'
                        }}>
                          <div>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              marginBottom: '4px' 
                            }}>
                              <span style={{ 
                                fontSize: '11px', 
                                color: 'rgba(255, 255, 255, 0.6)', 
                                background: 'rgba(255, 255, 255, 0.1)', 
                                padding: '2px 6px', 
                                borderRadius: '3px',
                                marginRight: '8px'
                              }}>
                                {kpi.code}
                              </span>
                              <span style={{ 
                                fontSize: '12px', 
                                color: 'rgba(0, 200, 255, 0.8)', 
                                background: 'rgba(0, 200, 255, 0.1)', 
                                padding: '2px 6px', 
                                borderRadius: '3px' 
                              }}>
                                {kpi.category}
                              </span>
                            </div>
                            <span style={{ 
                              fontWeight: 'bold', 
                              fontSize: '14px',
                              color: 'rgba(255, 255, 255, 0.9)' 
                            }}>
                              {kpi.description || 'Unnamed KPI'}
                            </span>
                          </div>
                          <button
                            onClick={() => toggleKPISelection(kpi)}
                            style={{
                              background: 'rgba(255, 100, 100, 0.2)',
                              border: '1px solid rgba(255, 100, 100, 0.4)',
                              color: 'rgba(255, 100, 100, 0.9)',
                              borderRadius: '3px',
                              padding: '4px 8px',
                              fontSize: '11px',
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Add New Validation Rule Button */}
            {selectedBrand && (
              <div style={{ 
                marginTop: '20px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <button 
                  className="primary-button"
                  onClick={handleCreateRule}
                  disabled={isFormOpen || !selectedBrand || selectedKPIs.length === 0}
                  style={{
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Add New Validation Rule
                </button>
                {selectedKPIs.length === 0 && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.6)'
                  }}>
                    Please select at least one KPI to create a validation rule
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {isFormOpen ? (
              <ValidationRuleForm 
                rule={editingRule}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                selectedBrand={selectedBrand}
                selectedKPIs={selectedKPIs}
                existingRules={rules}
              />
            ) : (
              <>
                {selectedBrand && (
                  <div style={{ 
                    marginBottom: '20px', 
                    padding: '12px 16px', 
                    background: 'rgba(0, 20, 40, 0.6)', 
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 200, 255, 0.3)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>Showing validation rules for:</strong> {selectedBrand} 
                        <span style={{ marginLeft: '10px', color: 'rgba(255, 255, 255, 0.7)' }}>
                          ({rules.filter(rule => rule.brand === selectedBrand).length} rules)
                        </span>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '15px',
                        fontSize: '12px'
                      }}>
                        {(() => {
                          const brandRules = rules.filter(rule => rule.brand === selectedBrand);
                          const totalWeight = brandRules.reduce((sum, rule) => sum + (rule.weight || 0), 0);
                          const remainingWeight = 100 - totalWeight;
                          return (
                            <>
                              <span style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                Total Weight: <strong>{totalWeight}/100</strong>
                              </span>
                              <span style={{ 
                                color: remainingWeight <= 10 ? 'rgba(255, 100, 100, 0.9)' : 'rgba(0, 200, 255, 0.9)' 
                              }}>
                                Remaining: <strong>{remainingWeight}</strong>
                              </span>
                              <div style={{
                                width: '80px',
                                height: '6px',
                                background: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '3px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${totalWeight}%`,
                                  height: '100%',
                                  background: totalWeight > 90 ? '#ff6b6b' : totalWeight > 70 ? '#ffa726' : '#4fc3f7',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
                <ValidationRuleTable
                  rules={selectedBrand ? rules.filter(rule => rule.brand === selectedBrand) : rules}
                  loading={loading}
                  onEdit={handleEditRule}
                  onDelete={handleDeleteRule}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPIMaintenancePage; 